import React, { useEffect, useMemo, useState } from 'react';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import { Property } from '../types';
import L from 'leaflet';
import { Sun, Moon, MapPin } from 'lucide-react';

// --- CUSTOM ICONS ---
const createCustomIcon = (color: string) => {
  return L.divIcon({
    className: 'custom-marker',
    html: `
      <div style="
        background-color: ${color};
        width: 24px;
        height: 24px;
        border-radius: 50% 50% 50% 0;
        transform: rotate(-45deg);
        border: 2px solid white;
        box-shadow: 0 4px 6px rgba(0,0,0,0.3);
        display: flex;
        align-items: center;
        justify-content: center;
      ">
        <div style="
          width: 8px;
          height: 8px;
          background-color: white;
          border-radius: 50%;
          transform: rotate(45deg);
        "></div>
      </div>
    `,
    iconSize: [24, 24],
    iconAnchor: [12, 24],
    popupAnchor: [0, -24]
  });
};

const icons = {
  vacant: createCustomIcon('#10b981'),     
  occupied: createCustomIcon('#ef4444'),   
  maintenance: createCustomIcon('#f59e0b') 
};

interface PropertyMapProps {
  properties: Property[];
  onSelectProperty?: (property: Property) => void;
}

const MapUpdater: React.FC<{ center: [number, number], zoom: number }> = ({ center, zoom }) => {
  const map = useMap();
  useEffect(() => {
    map.setView(center, zoom);
  }, [center, zoom, map]);
  return null;
};

const PropertyMap: React.FC<PropertyMapProps> = ({ properties, onSelectProperty }) => {
  const [isDarkMode, setIsDarkMode] = useState(true);

  const validProperties = useMemo(() => {
    return properties.filter(p => 
      p.lat !== undefined && p.lat !== null && p.lat !== 0 &&
      p.lng !== undefined && p.lng !== null && p.lng !== 0
    );
  }, [properties]);

  // LÓGICA DE CENTRO ATUALIZADA:
  // Se houver apenas 1 propriedade, foca nela com zoom detalhado (13).
  // Se houver várias (ou nenhuma), foca em Portugal Inteiro com zoom aberto (7).
  const mapState = useMemo(() => {
    if (validProperties.length === 1) {
      return { 
        center: [validProperties[0].lat!, validProperties[0].lng!] as [number, number], 
        zoom: 13 
      };
    } else {
      // Centro Geográfico de Portugal Continental (aprox.)
      return { 
        center: [39.6, -8.0] as [number, number], 
        zoom: 7 // Zoom 7 mostra PT inteiro + parte de ES e Oceano
      };
    }
  }, [validProperties]);

  const tileLayerUrl = isDarkMode 
    ? "https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png"
    : "https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png";

  return (
    <div className="h-full w-full relative z-0 group">
      
      <button 
        onClick={() => setIsDarkMode(!isDarkMode)}
        className="absolute top-4 right-4 z-[1000] bg-white/90 dark:bg-slate-800/90 backdrop-blur p-2 rounded-lg shadow-xl border border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300 hover:text-emerald-500 transition-colors"
      >
        {isDarkMode ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
      </button>

      {validProperties.length === 0 && (
        <div className="absolute inset-0 flex items-center justify-center z-[999] bg-slate-900/80 backdrop-blur-sm pointer-events-none">
          <div className="text-center bg-slate-900 p-6 rounded-2xl border border-slate-800 shadow-2xl">
            <div className="bg-slate-800 p-3 rounded-full inline-flex mb-3">
              <MapPin className="w-6 h-6 text-slate-500" />
            </div>
            <p className="text-slate-300 font-bold">No locations found</p>
            <p className="text-xs text-slate-500 mt-1 max-w-[200px]">
              Edit your properties and select an address from the list to save coordinates.
            </p>
          </div>
        </div>
      )}

      <MapContainer 
        center={mapState.center} 
        zoom={mapState.zoom} 
        scrollWheelZoom={true} 
        style={{ height: '100%', width: '100%', zIndex: 0, background: isDarkMode ? '#1e293b' : '#f1f5f9' }}
      >
        <TileLayer
          attribution='&copy; <a href="https://carto.com/">CARTO</a>'
          url={tileLayerUrl}
        />
        
        <MapUpdater center={mapState.center} zoom={mapState.zoom} />

        {validProperties.map((property) => {
          let icon = icons.vacant;
          if (property.status === 'Occupied') icon = icons.occupied;
          if (property.status === 'Maintenance') icon = icons.maintenance;

          return (
            <Marker 
              key={property.id} 
              position={[property.lat!, property.lng!]}
              icon={icon}
              eventHandlers={{
                click: () => onSelectProperty && onSelectProperty(property),
              }}
            >
              <Popup className="custom-popup" closeButton={false}>
                <div className="p-2 min-w-[160px]">
                  <h3 className="font-bold text-slate-900 text-sm leading-tight mb-1">{property.address}</h3>
                  <p className="text-[10px] text-slate-500 uppercase tracking-wider mb-2">{property.city}</p>
                  
                  <div className="flex items-center justify-between pt-2 border-t border-gray-100">
                     <div className="flex flex-col">
                        <span className="text-[10px] text-slate-400">Value</span>
                        <span className="text-xs font-bold text-emerald-600">€{property.currentValue.toLocaleString()}</span>
                     </div>
                     <span className={`text-[10px] px-2 py-0.5 rounded-full font-bold text-white shadow-sm ${
                       property.status === 'Occupied' ? 'bg-red-500' : 
                       property.status === 'Maintenance' ? 'bg-amber-500' : 'bg-emerald-500'
                     }`}>
                       {property.status}
                     </span>
                  </div>
                </div>
              </Popup>
            </Marker>
          );
        })}
      </MapContainer>
    </div>
  );
};

export default PropertyMap;